import { VizConfig } from "@/shared/config/VizConfig";
import { LayoutDefinitionBuilder, LayoutRecord } from "@/shared/domain/layouts";
import { LayoutDefinition } from "@/shared/domain/layouts/LayoutDefinition";
import { LoggerService } from "@/shared/services";
import { type VizStateChangeSet } from "@/shared/state/visualization/diffVizState";
import { PanelState } from "@/shared/state/visualization/schema";

/**
 * Rebuilds a stored layout to match the current workspace, as when a user saves
 * their workspace over a layout they already saved.
 *
 * An empty changeset returns the stored definition untouched, still carrying the
 * schema version it was read at; anything else comes back rewritten to the latest
 * schema version. Panels the changeset adds or changes have their rules re-derived
 * from the topics and message paths the panel currently shows plus its style, and
 * rules left unreferenced by any panel are dropped. The panel tree is taken from
 * the changeset wholesale, so a panel added here reaches the tree only when the
 * changeset also carries a layout.
 *
 * @throws {InvalidLayoutDefinitionError} if the stored definition does not match
 *   the schema registered for `schemaVersion`
 * @throws {UnsupportedLayoutSchemaVersion} if no schema is registered for
 *   `schemaVersion`
 */
export function applyChangesToLayout(
  existingLayoutDefinition: LayoutRecord["layout_definition"],
  schemaVersion: LayoutRecord["schema_version"],
  diff: VizStateChangeSet,
): LayoutDefinition {
  LoggerService.log("Applying changes to layout:", { diff });
  LoggerService.log("Existing layout:", existingLayoutDefinition);

  const oldLayoutDefinition = LayoutDefinition.fromObj(
    existingLayoutDefinition,
    schemaVersion,
  );

  if (!diff.hasChanges) {
    return oldLayoutDefinition;
  }

  const builder = new LayoutDefinitionBuilder(oldLayoutDefinition);

  Object.keys(diff.panels.removed).forEach((panelId) => {
    builder.removePanel(panelId);
  });

  Object.entries(diff.panels.changed).forEach(([panelId, panelState]) => {
    // A panel's data turns into the rules built below, and initParams carries
    // session-only state such as a panel's starting viewport, so neither is
    // stored on the panel.
    const { data: _, initParams: __, ...panelMetadata } = panelState;
    const ruleIds = addNewRules(builder, panelState);
    builder.updatePanel(panelId, panelMetadata, ruleIds);
  });

  Object.entries(diff.panels.added).forEach(([_, panelState]) => {
    const { data: __, initParams: ___, ...panelMetadata } = panelState;
    const ruleIds = addNewRules(builder, panelState);
    builder.addPanel(panelMetadata, ruleIds);
  });

  if (diff.layout.changed) {
    builder.setLayout(diff.layout.changed);
  }

  return builder.build();
}

function addNewRules(
  builder: LayoutDefinitionBuilder,
  panelState: PanelState,
): string[] {
  const panelDataAndStyles = VizConfig.getDataAndStylesForPanel(panelState);

  return panelDataAndStyles.map(({ data, style }) => {
    const requirementSet = data.map((topicData) =>
      LayoutDefinitionBuilder.createRequirementFromTopicData(topicData),
    );
    return builder.addRule(requirementSet, style);
  });
}
